/** biome-ignore-all lint/complexity/noExcessiveLinesPerFunction: too many lines */
import cx from 'classnames';
import {FocusTrap} from 'focus-trap-react';
import {DateTime} from 'luxon';
import {
  type FC,
  type MouseEventHandler,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  END_BEFORE_START_ERROR,
  PAST_EVENT_DATE_ERROR,
} from '~/common/FabricInvitation/utils/validation';
import {Button} from '~library/atoms/Button';
import {Input} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';
import {ResponsivePanel} from '~library/organisms/ResponsivePanel';
import {defaultTimes} from '~library/utils/constants';
import {DateTimeControl} from './DateTimeControl';
import {useDateTimeManagement} from './hooks/useDateTimeManagement';
import {Tabs} from './Tabs';
import {TimezoneDisplay} from './TimezoneDisplay';
import {TimezoneSelectorContent} from './TimezoneSelectorContent';
import type {StartEndControllerTab} from './types';
import {DEFAULT_TIME_CONFIG, findTimezoneObject, getDefaultTimezones} from './utils';

import './StartEndController.sass';

const validateStartDate = (start: DateTime | null): string | null =>
  !!start && start.diffNow('days').days < 0 ? PAST_EVENT_DATE_ERROR : null;
const validateEndDate = (start: DateTime | null, end: DateTime | null): string | null =>
  !!end && !!start && start.diff(end).milliseconds > 0 ? END_BEFORE_START_ERROR : null;

interface StartEndControllerProps {
  startDatetime?: DateTime;
  endDatetime?: DateTime;
  timezone: string;
  times?: string[];
  timezones?: TimezoneOption[];
  onSave: (params: {
    start: DateTime | null;
    end: DateTime | null;
    timezone: Timezone | null;
  }) => void;
  scrollContainerSelector?: string;
  // biome-ignore lint/style/useNamingConvention: allow pascal case?
  TriggerElement?: ReactNode;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  className?: string;
  datetimeInitFn?: (tab: StartEndControllerTab) => void;
  qaIdPrefix?: string;
  ref?: React.Ref<HTMLDivElement>;
  validator?: (start: string | null, end: string | null) => string | null;
}

export const StartEndController: FC<StartEndControllerProps> = ({
  startDatetime,
  endDatetime,
  timezone,
  times = defaultTimes as string[],
  timezones = getDefaultTimezones(),
  onSave,
  scrollContainerSelector,
  TriggerElement,
  open,
  setOpen,
  className,
  datetimeInitFn,
  qaIdPrefix = 'event',
  validator,
  ref,
}) => {
  const trigger = useRef<HTMLDivElement>(null);
  const defaultTriggerInput = useRef<HTMLInputElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  const [start, setStart] = useState<DateTime | null>(startDatetime ?? null);
  const [end, setEnd] = useState<DateTime | null>(endDatetime ?? null);
  const [tz, setTZ] = useState<Timezone | null>(findTimezoneObject(timezone) ?? null);
  const [isControllerOpen, setIsControllerOpen] = useState<boolean>(false);
  const [isTzSelectorOpen, setIsTzSelectorOpen] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<StartEndControllerTab>('start');

  const {changeDate, changeTime} = useDateTimeManagement(start, end, tz, selectedTab);
  const isDesktop = useIsLargishAndUp();

  useEffect(() => {
    if (open === undefined) return;
    setIsControllerOpen(open);
  }, [open]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    setStart(startDatetime ?? null);
  }, [startDatetime, isControllerOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    setEnd(endDatetime ?? null);
  }, [endDatetime, isControllerOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    setTZ(findTimezoneObject(timezone) ?? null);
  }, [timezone, isControllerOpen]);

  const initializeTimeForTab = useCallback(
    (tab: StartEndControllerTab) => {
      if (datetimeInitFn) {
        // If a custom initialization function is provided, use it instead of default logic
        datetimeInitFn(tab);
        return;
      }
      if (tab === 'end' && !end) {
        let defaultEnd: DateTime;
        if (start) {
          defaultEnd = start.plus({hours: 3}); // Default end time is 3 hours after start
        } else {
          defaultEnd = DateTime.now().set(DEFAULT_TIME_CONFIG); // If no start, set end to today at 6pm
        }
        if (isDesktop) {
          onSave({start, end: defaultEnd, timezone: tz});
        } else {
          setEnd(defaultEnd);
        }
      }
    },
    [datetimeInitFn, end, isDesktop, onSave, start, tz]
  );

  useEffect(() => {
    if (!isControllerOpen) return;
    initializeTimeForTab('start');
  }, [initializeTimeForTab, isControllerOpen]);

  const handleTabChange = useCallback(
    (tab: StartEndControllerTab) => {
      setSelectedTab(tab);
      initializeTimeForTab(tab);
    },
    [initializeTimeForTab]
  );

  const toggleControllerOpenState = useCallback(
    (state: boolean) => {
      if (setOpen) setOpen(state);
      else setIsControllerOpen(state);
    },
    [setOpen]
  );

  const handleSave = useCallback(() => {
    if (!tz) return;
    onSave({start, end, timezone: tz});
    toggleControllerOpenState(false);
  }, [onSave, start, end, tz, toggleControllerOpenState]);

  const handleDateChange = useCallback(
    (date: Date) => {
      const payload = changeDate(date);
      if (isDesktop) onSave(payload);
      else {
        if (selectedTab === 'start') setStart(payload.start);
        setEnd(payload.end);
      }
    },
    [changeDate, isDesktop, onSave, selectedTab]
  );

  const handleTimeChange = useCallback(
    (time: string) => {
      const payload = changeTime(time);
      if (isDesktop) onSave(payload);
      else {
        if (selectedTab === 'start') setStart(payload.start);
        setEnd(payload.end);
      }
    },
    [changeTime, isDesktop, onSave, selectedTab]
  );

  const onClearEnd = useCallback(() => {
    const payload = {start, end: null, timezone: tz};
    if (isDesktop) onSave(payload);
    else setEnd(null);
    setSelectedTab('start');
  }, [start, tz, onSave, isDesktop]);

  const ControllerContent = useMemo(() => {
    const startDateError = validateStartDate(start);
    const endDateError = validateEndDate(start, end);
    return (
      <>
        <div className="start-end-controller" id="start-end-controller">
          <Tabs
            start={start}
            end={end}
            selectedTab={selectedTab}
            onTabClick={handleTabChange}
            onClearEnd={onClearEnd}
            startError={!!startDateError}
            endError={!!endDateError}
            qaIdPrefix={qaIdPrefix}
          />
          <DateTimeControl
            selectedTab={selectedTab}
            selectedDateTime={selectedTab === 'start' ? start : end}
            minDate={selectedTab === 'end' ? start : null}
            times={times}
            onChangeDate={handleDateChange}
            onChangeTime={handleTimeChange}
            open={isControllerOpen}
          />
          {isDesktop ? (
            <div>
              <TimezoneDisplay
                timezone={tz}
                openTimezoneSelector={() => setIsTzSelectorOpen(true)}
              />
            </div>
          ) : null}
        </div>
        {!isDesktop && (
          <div className="start-end-controller__timezone-container">
            <TimezoneDisplay timezone={tz} openTimezoneSelector={() => setIsTzSelectorOpen(true)} />
            <Button
              variant="primary"
              onClick={handleSave}
              size="small"
              data-qa-id={`${qaIdPrefix}-save-button`}
              disabled={Boolean(!start || startDateError || endDateError)}
            >
              Save
            </Button>
          </div>
        )}
      </>
    );
  }, [
    start,
    end,
    selectedTab,
    onClearEnd,
    handleTabChange,
    times,
    handleDateChange,
    handleTimeChange,
    isControllerOpen,
    tz,
    isDesktop,
    handleSave,
    qaIdPrefix,
  ]);

  const formattedStartEnd = useMemo(() => {
    const tzAbbr = tz?.timezone_abbr;
    if (!start) return '';
    if (!end) return `${start.toFormat('EEE MMM d, h:mm a')} ${tzAbbr ?? ''}`.trim();
    if (start.day === end.day && start.month === end.month && start.year === end.year) {
      // If start and end are on the same day, format as a range
      return `${start.toFormat('EEE MMM d, h:mm a')} - ${end.toFormat('h:mm a')} ${tzAbbr ?? ''}`.trim();
    }
    return `${start.toFormat('EEE MMM d, h:mm a')} ${tzAbbr ?? ''} - ${end.toFormat('EEE MMM d, h:mm a')} ${tzAbbr ?? ''}`.trim();
  }, [start, end, tz?.timezone_abbr]);

  const openController: MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setSelectedTab('start');
      toggleControllerOpenState(true);
    },
    [toggleControllerOpenState]
  );

  const closeController = useCallback(() => {
    toggleControllerOpenState(false);
    setSelectedTab('start');
  }, [toggleControllerOpenState]);

  const closeTimezoneSelector = useCallback(() => {
    setIsTzSelectorOpen(false);
  }, []);

  const DefaultTrigger = useMemo(() => {
    let error: string | null = null;
    if (validator) {
      error = validator(start?.toISO() ?? null, end?.toISO() ?? null);
    } else {
      const startDateError = validateStartDate(start);
      const endDateError = validateEndDate(start, end);
      error = startDateError || endDateError;
    }
    return (
      <div className="start-end-controller__trigger-wrapper">
        <Typography variant="label2" size="small" className="input-label">
          Date & Time*
        </Typography>
        <Button
          className="start-end-controller__trigger"
          variant="unstyled-button"
          id="date-time"
          data-qa-id={`${qaIdPrefix}-date-trigger`}
          type="button"
          onClick={openController}
        >
          <Input
            readonly
            data-qa-id={`${qaIdPrefix}-date-input-box`}
            required
            value={formattedStartEnd}
            placeholder="Pick a date & time"
            error={Boolean(error)}
            tabIndex={-1}
            ref={defaultTriggerInput}
          />
        </Button>
        {error && (
          <span
            className="input-error start-end-controller__error-message"
            data-qa-id={`${qaIdPrefix}-date-input-box__inputErrorMessage`}
            role="region"
            aria-live="polite"
            aria-relevant="additions"
          >
            {error}
          </span>
        )}
      </div>
    );
  }, [formattedStartEnd, openController, start, end, qaIdPrefix, validator]);

  const onTimezoneChange = useCallback(
    (newTZ: Timezone | null) => {
      if (isDesktop) {
        const payload = {start, end, timezone: newTZ};
        onSave(payload);
      } else {
        setTZ(newTZ);
      }
      setIsTzSelectorOpen(false);
    },
    [isDesktop, start, end, onSave]
  );

  const MemoizedTzSelectorContent = useMemo(
    () => (
      <TimezoneSelectorContent
        timezones={timezones}
        selectedTimezone={tz}
        onTimezoneChange={onTimezoneChange}
        open={isTzSelectorOpen}
      />
    ),
    [timezones, tz, onTimezoneChange, isTzSelectorOpen]
  );

  const ControllerOverlay = useMemo(
    () =>
      isDesktop ? (
        <>
          <FocusTrap active={isControllerOpen}>
            <div
              className={cx(`start-end-controller__desktop`, {open: isControllerOpen})}
              data-qa-id="start-end-controller"
              ref={popover}
            >
              {ControllerContent}
            </div>
          </FocusTrap>
          <ResponsiveModal
            isOpen={isTzSelectorOpen}
            onClose={closeTimezoneSelector}
            title="Event time zone"
            modalId={`${qaIdPrefix}-time-zone`}
          >
            {MemoizedTzSelectorContent}
          </ResponsiveModal>
        </>
      ) : (
        <ResponsivePanel
          isOpen={isControllerOpen || isTzSelectorOpen}
          onClose={isTzSelectorOpen ? closeTimezoneSelector : closeController}
          title={isTzSelectorOpen ? 'Event time zone' : 'Date and time'}
          renderMobileViewAs="bottom-sheet"
          modalId="start-end-controller"
          hideHeaderDivider
        >
          {isTzSelectorOpen ? MemoizedTzSelectorContent : ControllerContent}
        </ResponsivePanel>
      ),
    [
      isDesktop,
      isControllerOpen,
      isTzSelectorOpen,
      closeTimezoneSelector,
      qaIdPrefix,
      MemoizedTzSelectorContent,
      ControllerContent,
      closeController,
    ]
  );

  const pinStartEndControllerToInput = useCallback(() => {
    if (!trigger.current || !popover.current) return;
    const triggerRect = trigger.current.getBoundingClientRect();
    if (triggerRect.left < 500) {
      // StartEndController popover passes left edge of window
      popover.current.style.top = `${triggerRect.top}px`;
      popover.current.style.left = `${triggerRect.right + 4}px`;
      popover.current.style.right = 'unset';
    } else {
      popover.current.style.top = `${triggerRect.top + 18}px`;
      popover.current.style.right = `${triggerRect.width + 64}px`;
      popover.current.style.left = 'unset';
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (!isDesktop) return undefined;
    const scrollContainer = scrollContainerSelector
      ? document.querySelector(scrollContainerSelector)
      : document.body;
    if (!scrollContainer) return undefined;

    pinStartEndControllerToInput();
    scrollContainer.addEventListener('scroll', pinStartEndControllerToInput);
    window.addEventListener('resize', pinStartEndControllerToInput);
    return () => {
      scrollContainer.removeEventListener('scroll', pinStartEndControllerToInput);
      window.removeEventListener('resize', pinStartEndControllerToInput);
    };
  }, [isDesktop, isControllerOpen, scrollContainerSelector, pinStartEndControllerToInput]);

  useEffect(() => {
    if (!isDesktop || !isControllerOpen || isTzSelectorOpen) return undefined;
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const elem = event.target as Element;
      if (!elem.matches('.start-end-controller__wrapper, .start-end-controller__wrapper *')) {
        closeController();
      }
    };

    document.body.addEventListener('mousedown', handleClickOutside);
    document.body.addEventListener('touchend', handleClickOutside);
    return () => {
      document.body.removeEventListener('mousedown', handleClickOutside);
      document.body.removeEventListener('touchend', handleClickOutside);
    };
  }, [isControllerOpen, isTzSelectorOpen, isDesktop, closeController]);

  useEffect(() => {
    if (!isControllerOpen) return undefined;

    const closeOnEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isTzSelectorOpen) closeTimezoneSelector();
      else closeController();
    };

    document.body.addEventListener('keydown', closeOnEsc);
    return () => {
      document.body.removeEventListener('keydown', closeOnEsc);
    };
  }, [isControllerOpen, isTzSelectorOpen, closeController, closeTimezoneSelector]);

  useEffect(() => {
    if (!isControllerOpen) return;
    const startTab = document.querySelector(
      `[data-qa-id="${qaIdPrefix}-start-tab"]`
    ) as HTMLElement;
    if (!startTab) return;
    startTab?.focus();
  }, [isControllerOpen, qaIdPrefix]);

  useEffect(() => {
    if (!defaultTriggerInput.current) return;
    defaultTriggerInput.current.classList[isControllerOpen ? 'add' : 'remove']('focused');
  }, [isControllerOpen]);

  if (TriggerElement)
    return (
      <div className="start-end-controller__wrapper" ref={trigger}>
        {TriggerElement}
        {ControllerOverlay}
      </div>
    );

  return (
    <div className={cx('start-end-controller__wrapper', className)} ref={ref}>
      <div ref={trigger}>{TriggerElement ?? DefaultTrigger}</div>
      {ControllerOverlay}
    </div>
  );
};
